import { ProductConfig } from "../product.config";

/**
 * Meetings Product Configuration
 *
 * Domains:
 * - Production: globalcoders.app
 * - Preview: preview--speakglobalcoders.lovable.app
 * - Dev: localhost
 *
 * Note: This is one of three products (meetings, interview, recruiters)
 */
export const recruitersConfig: ProductConfig = {
  id: "recruiters",
  name: "recruiters",
  displayName: "GlobalCoders",

  collectionType: "recruiters", // Firestore collection name for product data

  theme: {
    primary: "225 75% 35%", // Current blue
    accent: "270 65% 50%", // Current purple
  },

  i18n: {
    // App branding
    appName: "GlobalCoders",
    appTagline: "English conversation practice for tech teams",

    // Navigation & Common labels
    dashboard: "Dashboard",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    backToHome: "Back to Home",

    // Auth pages
    loginTitle: "Welcome!",
    loginSubtitle: "Sign in to continue",
    signupTitle: "Get Started",
    signupSubtitle: "Create your account to begin",
    signInToContinue: "Sign in to continue",
    welcomeBack: "Welcome Back",

    // Meeting specific
    preparationTitle: "Meeting Preparation",
    simulationTitle: "Meeting Simulation",
    feedbackTitle: "Meeting Feedback",
    startPreparation: "Start Preparation",
    startSimulation: "Start Simulation",
    continueSimulation: "Continue Simulation",

    // Paywall & Premium
    unlockPremiumFeatures: "Unlock Premium Features",
    upgradeToPremium: "Upgrade to Premium",
    premiumFeatures: "Premium",
    premiumDescription:
      "Get unlimited access to advanced AI feedback, premium content, and exclusive features.",
    goToDashboard: "Go to Dashboard",
    viewProfile: "View Profile",

    // Actions
    start: "Start",
    continue: "Continue",
    finish: "Finish",
    cancel: "Cancel",
    save: "Save",
    loading: "Loading...",
    tryAgain: "Try Again",

    // Success messages
    welcomeMessage: "Welcome to GlobalCoders!",
    welcomeToPremium: "Welcome to Premium!",
    paymentSuccessful: "Payment processed successfully! Welcome to premium.",
    subscriptionActivated:
      "Your premium subscription has been activated successfully. You now have access to all premium features.",

    // Error messages
    genericError: "Something went wrong. Please try again.",
    networkError: "Network error. Please check your connection.",
    failedToLoadCategories: "Failed to load categories",
    noConversationCategories: "No conversation categories available yet.",

    // Profile page
    profilePhoto: "Profile Photo",
    basicInformation: "Basic Information",
    subscriptionStatus: "Subscription Status",

    // Loading states
    loadingSubscription: "Loading subscription options...",

    // Landing page - Hero Section
    hero: {
      title: "English Conversation Practice",
      subtitle: "for Tech Recruiters",
      description:
        "AI conversation practice in a safe environment. Simulate conversations with hiring managers and candidates and ace your next conversation!",
      ctaButton: "Start Practicing Now",
    },

    // Landing page - Categories Section
    categories: {
      badge: "CONVERSATION CATEGORIES",
      title: "Practice Real Work Scenarios",
      subtitle:
        "Choose from a wide range of realistic conversation scenarios across different categories",
      subtitleDynamic: (count: number) =>
        `Choose from a wide range of realistic conversation scenarios across different categories`,
      subtitleFallback:
        "Choose from a wide range of realistic conversation scenarios across different categories",
    },

    // Landing page - Testimonials Section
    testimonials: {
      badge: "TESTIMONIALS",
      title: "What Our Users Say",
      subtitle:
        "Join thousands of developers improving their English communication skills",
      testimonial1: {
        quote:
          "This platform helped me overcome my fear of speaking in daily standups. Now I feel confident sharing my progress with the team.",
        name: "Lorena",
        role: "Frontend Developer",
        company: "Tech Startup",
      },
      testimonial2: {
        quote:
          "The AI feedback is incredibly helpful. I've improved my pronunciation and learned professional phrases I use in real meetings now.",
        name: "Steve",
        role: "Backend Engineer",
        company: "Fortune 500",
      },
      testimonial3: {
        quote:
          "Practicing performance reviews here made the real conversation so much easier. I got the promotion I was hoping for!",
        name: "Samir",
        role: "Senior Developer",
        company: "SaaS Company",
      },
    },

    // Landing page - Data Protection Section
    dataProtection: {
      badge: "YOUR DATA IS SAFE",
      title: "Privacy & Security First",
      subtitle:
        "Your conversations are private and secure. We take data protection seriously.",
      encryption: {
        title: "Database Encryption",
        description:
          "Your data is securely stored in an encrypted database using industry-standard protocols.",
      },
      privacy: {
        title: "Complete Privacy",
        description:
          "Only you have access to your practice sessions. We never share your data with third parties",
      },
      storage: {
        title: "Secure Storage",
        description:
          "Your conversations are safely stored with enterprise-grade security",
      },
      bottomMessage:
        "We store data safely and only you have access to it. Your practice sessions are completely private and confidential.",
    },

    // Landing page - How It Works Section
    howItWorks: {
      badge: "SIMPLE PROCESS",
      title: "Get Started in 3 Easy Steps",
      subtitle: "Deploy confidence in every conversation.",
      step1: {
        title: "Choose Your Scenario",
        description:
          "Select from a wide range of conversation types tailored to tech recruiters",
      },
      step2: {
        title: "Practice with AI",
        description:
          "Engage in realistic conversations with our intelligent AI simulator",
      },
      step3: {
        title: "Improve & Repeat",
        description:
          "Review feedback, refine your approach, and build lasting confidence",
      },
    },

    // Landing page - CTA Section
    cta: {
      title: "Ready to Transform Your Communication?",
      subtitle:
        "Join thousands of professionals who are mastering their conversations with AI-powered practice",
      button: "Start Practicing Now",
    },

    // Landing page - Footer
    footer: {
      companyName: "GlobalCoders",
      companyDescription:
        "AI-powered English conversation practice for tech professionals. Build confidence, improve communication, and ace your next conversation.",
      resources: "Resources",
      faq: "FAQ",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      subscriptionBilling: "Subscription and Billing Terms",
      contact: "Get in Touch",
      contactDescription:
        "Have questions or feedback? We'd love to hear from you. Contact us on support@globalcoders.app - we do respond to all emails!",
      fundingAlt:
        "Financiado por la Unión Europea - NextGenerationEU. Con el apoyo de la Generalitat de Catalunya",
      copyright: (year: number) =>
        `© ${year} Global Coders. All rights reserved.`,
      madeWith: "Made with ❤️ for developers worldwide",
    },

    // Dashboard page
    dashboardPage: {
      welcome: "Welcome back!",
      subtitle:
        "Ready to practice more conversations and improve your English skills?",
      totalSimulations: "Total Conversation Simulations",
      averageScore: "Average Score",
      lastPractice: "Last Practice",
      ctaEmoji: "",
      ctaTitle: "READY TO PRACTICE?",
      ctaSubtitle: "Continue Your Learning Journey",
      ctaDescription:
        "Practice with a wide range of realistic scenarios designed specifically for tech recruiters.",
      ctaButton: "Start New Practice Session",
      recentSessions: "Recent Practice Sessions",
      emptySessions: "No practice sessions yet",
      emptySessionsDescription:
        "Start your first simulation to see your progress here",
      startFirstSession: "Start First Session",
      topExpressions: "Top Expressions",
      viewFeedback: "View Feedback",
      score: "SCORE",
      tryAgainButton: "Try Again",
      refreshButton: "Refresh dashboard",
    },

    // Profile page
    profilePage: {
      loadingMessage: "Loading your profile and subscription details...",
      photoMaxSize: "Maximum file size: 5MB",
      invalidFileType: "Invalid file type. Please select an image file.",
      fileTooLarge: "File too large. Please select an image smaller than 5MB.",
      photoUpdated: "Profile photo updated successfully",
      uploadFailed: "Upload failed. Please try again.",
      updateFailed: "Update failed. Please try again.",
      namePlaceholder: "Enter your full name",
      emailPlaceholder: "Enter your email",
      subscriptionUnavailable:
        "Subscription Management Temporarily Unavailable",
      subscriptionUnavailableDesc:
        "Your subscription portal is not accessible at this time. This may be due to a recent account change. Please try again in a few minutes or contact support if the issue persists.",
      profileDataNotLoaded: "Profile data not loaded",
      success: "Success",
      profileUpdated: "Profile updated successfully",
      pageTitle: "Profile Settings",
      pageSubtitle: "Manage your account information and preferences",
      profilePhotoTitle: "Profile Photo",
      clickToUpload: "Click on the avatar to upload a new photo",
      basicInformationTitle: "Basic Information",
      fullName: "Full Name",
      emailAddress: "Email Address",
      subscriptionManagementTitle: "Subscription Management",
      premiumActive: "Premium Active",
      manageSubscriptionDesc:
        "Manage your subscription, update payment methods, view invoices, and more.",
      loading: "Loading...",
      manageYourSubscription: "Manage Your Subscription",
      unlockPremiumDesc:
        "Unlock premium features and get unlimited access to all content.",
      getPremium: "Get Premium",
      cancel: "Cancel",
      saving: "Saving...",
      saveChanges: "Save Changes",
      uploadingProgress: (percent: number) => `Uploading... ${percent}%`,
    },

    // Meeting pages
    meetingSimulator: {
      heroTitle: "AI Conversation Simulator",
      heroSubtitle:
        "Practice and master your professional communication skills with AI-powered conversation simulations.",
      heroSubtitleWithCount: (count: number) =>
        `Practice and master your professional communication skills with AI-powered conversation simulations. Choose from 50+ scenarios designed for tech recruiters.`,
      searchPlaceholder: "Search for a conversation...",
      loadingMeetings: "Loading meetings...",
      noMeetingsFound: (query: string) =>
        `No meetings found matching "${query}"`,
      resources: "Resources",
    },

    meetingCover: {
      loadingDetails: "Loading meeting details...",
      notFound: "Meeting not found",
      backToAll: "Back to all meetings",
      categoryBadge: (category: string) => category,
      prepareFirstTitle: "Prepare First",
      prepareFirstDescription:
        "We help you structure your answers and practice your pronunciation before the simulation.",
      prepareButtonLabel: "Start Preparation",
      startSimulationTitle: "Start Simulation",
      startSimulationDescription:
        "Jump directly into the AI-powered simulation and receive detailed feedback on your communication skills.",
      startSimulationButtonLabel: "Start Simulation",
      backToAllConversations: "Back to all conversations",
      prepareYourself:
        "Prepare yourself for this conversation or jump straight into the simulation",
    },

    meetingPreparation: {
      answerPlaceholder: "Type your answer in any language....",
    },

    meetingPreparationSummary: {
      loadingDetails: "Loading meeting details...",
      notFound: "Meeting not found",
      noData: "No preparation data found",
      incomplete: "Incomplete preparation",
      configError: "Configuration error",
      submitSuccess: "Your preparation has been submitted successfully",
      submitFailed: "Submission failed. Please try again.",
      tryExpressionsAgain: "Try Expressions Again",
      practiceExpressions: "Pronunciation Practice",
      submitting: "Submitting your answers...",
      submittingDesc: "Please wait while we process your preparation.",
      generatingExpressions: "Generating expressions with AI...",
      generatingExpressionsDesc:
        "Our AI is analyzing your answers and creating personalized expressions. This may take up to 80 seconds.",
      attemptOf: (current: number, total: number) =>
        `Attempt ${current} of ${total}`,
      loadingAudio: "Loading audio files...",
      loadingAudioDesc: (count: number) =>
        `Downloading ${count} audio files for instant playback.`,
      percentComplete: (percent: number) => `${percent}% complete`,
      submissionFailedTitle: "Submission Failed",
      unexpectedError: "An unexpected error occurred.",
      tryAgain: "Try Again",
      backToPreparation: "Back to Preparation",
      greatWork: "Great work!",
      yourExpressions: "Your AI-Generated Expressions",
      expressionsDesc:
        "Based on your answers, we've created these personalized expressions. Practice them before the simulation.",
      expression: "Expression",
      viewAll: (count: number) => `View All ${count} Expressions`,
      whatNext: "What would you like to do next?",
      practiceExpressionsDesc:
        "Review and practice pronunciation of your personalized expressions.",
      startSimulationDesc:
        "Ready to put your preparation into action? Start the live conversation simulation.",
      startSimulation: "Start Simulation",
    },

    meetingSimulation: {
      sessionError: "Session Error",
      sessionNotFound: "Meeting session not found",
      submissionFailed: "Submission Failed",
      hostSpeaking: "Host is speaking...",
      conversationCompleted: "Conversation completed! Processing...",
      clickToSpeak: "Click the microphone to speak",
      thankYou: "Thank you for completing the conversation",
      responsesAnalyzed: "Your responses have been analyzed",
    },

    // Interview pages
    interviewCompleted: {
      title: "Conversation Analysis",
      analyzing: "Analyzing Your Conversation...",
      complete: "Analysis Complete!",
      description: "Our AI is carefully evaluating your performance...",
      stepFlow: "Analyzing conversation flow",
      stepSkills: "Evaluating communication skills",
      stepFeedback: "Generating personalized feedback",
      stepReport: "Preparing your report",
      sessionError: "Session Error",
      sessionNotFound: "Meeting session not found. Redirecting...",
      processingError: "Processing Error",
      processingTakingLonger: "Processing Taking Longer",
      processingTakingLongerDesc:
        "Your assessment is still being processed. You can check back later.",
      noAssessmentFound: "No Assessment Found",
      noAssessmentFoundDesc: "Please complete an interview first.",
      backToDashboard: "Back to Dashboard",
      analysisFailed: "Analysis Failed",
      analysisFailedDesc:
        "We couldn't process your assessment. Please try again.",
    },

    interviewFeedback: {
      title: "Your Conversation Performance",
      invalidAssessment: "Invalid Assessment",
      loadFailed: "Failed to Load Feedback",
      shareTitle: "My Conversation Feedback",
      shareSuccess: "Shared Successfully",
      linkCopied: "Link copied to clipboard!",
      generatingPdf: "Generating PDF",
      pdfDownloaded: "PDF Downloaded",
      pdfSuccess: "Your feedback has been saved as PDF!",
      goToDashboard: "Go to Dashboard",
      pageTitle: "Conversation Feedback",
    },

    // Practice Expressions
    practiceExpressions: {
      loadingExpressions: "Loading expressions...",
      title: "Practice Your Expressions",
      subtitle:
        "Listen to each expression and practice your pronunciation. Click the microphone to record yourself.",
      noExpressionsTitle: "No Expressions Available",
      noExpressionsDescription:
        "Please complete the meeting preparation first to generate expressions.",
      goToPreparation: "Go to Preparation",
      backToDashboard: "Back to Dashboard",
      backToSummary: "Back to Summary",
      continueToSimulation: "Continue to Simulation",
      failedToLoad: "Failed to load expressions",
      tryAgainFromDashboard: "Please try again from the dashboard.",
    },

    // Checkout pages
    checkoutSuccess: {
      processing: "Processing payment...",
      processingMessage: "Please wait while we confirm your subscription.",
      approved: "Payment Approved!",
      activatedMessage:
        "Your premium subscription has been activated successfully. You now have access to all advanced platform features.",
      welcomeToPremium: "Welcome to Premium!",
      benefit1: "• Unlimited access to all meetings",
      benefit2: "• Detailed performance reports",
      benefit3: "• Priority support",
      benefit4: "• Exclusive AI features",
      goToDashboard: "Go to Dashboard",
      manageSubscription: "Manage Subscription",
      noSessionId: "Payment session not found",
      notAuthenticated: "User not authenticated",
      errorProcessing: "Error processing payment. Please contact support.",
    },

    checkoutCancelled: {
      title: "Checkout Cancelled",
      message:
        "The payment process has been cancelled. Don't worry, you haven't been charged and can try again whenever you want.",
      whyTitle: "Why cancel?",
      reason1: "• Changed your mind? No problem!",
      reason2: "• Want to choose another plan? We have options.",
      reason3: "• Questions about the service? Contact us.",
      reason4: "• You can come back and try again anytime.",
      backToDashboard: "Back to Dashboard",
      exploreFree: "Explore Free Resources",
      needHelp: "Precisa de ajuda? Entre em contato:",
      supportEmail: "support@globalcoders.app",
    },

    // Not Found page
    notFoundPage: {
      title: "404",
      message: "Oops! Page not found",
    },

    // Components
    paywallComponent: {
      loadingOptions: "Loading subscription options...",
      loadProductsFailed: "Failed to load products",
      checkoutFailed: "Failed to create checkout session",
    },

    loadingComponent: {
      defaultMessage: "Loading...",
      loadingResources: "Loading resources...",
    },

    assessmentComponent: {
      pronunciationError: "Error analyzing pronunciation. Please try again.",
    },

    assessmentResult: {
      finishPractice: "Finish Practice",
      continueNext: "Continue to Next Expression",
    },
  },

  assets: {
    // Future: custom logo/favicon paths
  },

  support: {
    whatsapp: {
      enabled: true,
      phoneNumber: "34613458043",
      defaultMessage: "👋 Hey! Can you tell me more about GlobalCoders?",
    },
    email: "support@globalcoders.app",
  },
};
